import type { Role } from '@insightai/api-client'
import { hasAtLeast } from '@insightai/auth'

export interface NavItem {
  id: string
  label: string
  /** Path relative to /w/:wsId, or absolute when it starts with '/'. */
  to: string
  icon: string
  /** Minimum role that sees this item. Affordance filtering only (AC-53). */
  minRole?: Role
  /** Exact role requirement, for the ops/admin surfaces. */
  requireRole?: Role
  /**
   * May also appear for a lower-ranked role when the workspace has a study in
   * design phase (§6.1's context rule). Set on the read-oriented methodology
   * surfaces; deliberately NOT set on the Research Lead work queue.
   */
  contextVisible?: boolean
  /** Modules that do not exist yet — rendered disabled with a phase note. */
  phase?: 1 | 2 | 3 | 4
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
  /** Methodology group appears for RESEARCH_LEAD+ or a study in design phase. */
  showWhen?: 'methodologyUser' | 'researchOps' | 'platformAdmin'
}

/**
 * F0-60 — ONE sidebar, RBAC- and context-filtered.
 *
 * §6.1 records that the wireframes imply two operational groupings — a
 * client-facing set and a methodology/ops set — and resolves it to a single
 * filtered sidebar rather than two hard-coded ones, because two would drift.
 * This module is that resolution, expressed as data so the filtering rule is
 * testable without rendering.
 *
 * `phase` marks modules that land later. They are rendered as disabled rows with
 * the phase named, rather than hidden: a client who was shown "Survey Studio" in
 * a demo should see it exists and is coming, not conclude it was removed. Hiding
 * them would also make the Phase 0 shell look complete when it is not.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'client',
    label: 'Research',
    items: [
      { id: 'home', label: 'Workspace', to: '', icon: 'LayoutDashboard' },
      { id: 'briefs', label: 'Briefs', to: '/briefs', icon: 'FileText' },
      { id: 'secondary', label: 'Secondary Desk', to: '/secondary', icon: 'Library', phase: 1 },
      { id: 'surveys', label: 'Survey Studio', to: '/surveys', icon: 'ClipboardList', phase: 2 },
      { id: 'descriptive', label: 'Descriptive Hub', to: '/descriptive', icon: 'BarChart3', phase: 4 },
      { id: 'insights', label: 'Insights', to: '/insights', icon: 'Sparkles', phase: 3 },
      { id: 'reports', label: 'Reports', to: '/reports', icon: 'FileBarChart', phase: 1 },
      { id: 'copilot', label: 'Copilot', to: '/copilot', icon: 'MessageSquare', phase: 1 },
      { id: 'kg', label: 'Knowledge Graph', to: '/kg', icon: 'Network', phase: 1 },
    ],
  },
  {
    id: 'methodology',
    label: 'Methodology & Ops',
    showWhen: 'methodologyUser',
    items: [
      // The RL work queue is never context-visible: it is someone's inbox.
      { id: 'review-queue', label: 'Review Queue', to: '/review-queue', icon: 'ListChecks', minRole: 'RESEARCH_LEAD' },
      { id: 'scaling', label: 'Scaling Engine', to: '/scaling', icon: 'Ruler', minRole: 'RESEARCH_LEAD', contextVisible: true, phase: 2 },
      { id: 'questionnaire-ai', label: 'Questionnaire AI', to: '/questionnaire-ai', icon: 'BrainCircuit', minRole: 'RESEARCH_LEAD', contextVisible: true, phase: 2 },
      { id: 'personas', label: 'AI Personas', to: '/personas', icon: 'Users', minRole: 'RESEARCH_LEAD', phase: 3 },
      { id: 'provenance', label: 'Provenance Audits', to: '/provenance', icon: 'ShieldCheck', minRole: 'RESEARCH_LEAD', contextVisible: true, phase: 1 },
    ],
  },
  {
    id: 'ops',
    label: 'Panel Operations',
    showWhen: 'researchOps',
    items: [
      { id: 'panel', label: 'Panel Health', to: '/ops/panel', icon: 'Activity', phase: 2 },
    ],
  },
  {
    id: 'admin',
    label: 'Platform Admin',
    showWhen: 'platformAdmin',
    items: [
      { id: 'model-registry', label: 'Model Registry', to: '/admin/model-registry', icon: 'Cpu', requireRole: 'PLATFORM_ADMIN', phase: 1 },
      { id: 'prompts', label: 'Prompt Registry', to: '/admin/prompts', icon: 'ScrollText', requireRole: 'PLATFORM_ADMIN', phase: 1 },
      { id: 'pricing', label: 'Pricing Rules', to: '/admin/pricing', icon: 'IndianRupee', requireRole: 'PLATFORM_ADMIN', phase: 1 },
      { id: 'validation-rules', label: 'Validation Rules', to: '/admin/validation-rules', icon: 'ShieldAlert', requireRole: 'PLATFORM_ADMIN', phase: 1 },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { id: 'settings', label: 'Settings', to: '/settings', icon: 'Settings' },
    ],
  },
]

export interface NavCapabilities {
  isMethodologyUser: boolean
  isResearchOps: boolean
  isPlatformAdmin: boolean
  /** Context signal from §6.1: a study in design phase surfaces methodology. */
  hasStudyInDesign?: boolean
  roles: Role[]
}

/**
 * Pure filter, so the §6.1 rule is unit-testable without a router or a DOM.
 *
 * THE CONTEXT RULE, resolved. §6.1 says the methodology group appears for
 * `RESEARCH_LEAD`+ "or when a study is in design phase". Taken literally that
 * conflicts with per-item role gating: a client editor would see the group with
 * every item filtered out. The resolution:
 *
 *   · Read-oriented methodology surfaces (Scaling Engine, Questionnaire AI,
 *     Provenance Audits) are marked `contextVisible` and appear for a
 *     CLIENT_EDITOR or above when a study is in design phase. That is the case
 *     §6.1 is describing — a client involved in study design needs to see the
 *     measurement and questionnaire decisions being made on their study.
 *   · The Review Queue never appears by context. It is a Research Lead's work
 *     queue, and showing someone else's inbox is not a useful affordance.
 *   · CLIENT_VIEW_ONLY gets none of it: they cannot act on any of these surfaces,
 *     so the group would be noise.
 *
 * This is an interpretation of an ambiguous requirement, and it belongs on the
 * §15 decision list for Product confirmation rather than being buried in code —
 * flagged in the tracker against F0-60.
 */
export function visibleNavGroups(capabilities: NavCapabilities): NavGroup[] {
  const { isMethodologyUser, isResearchOps, isPlatformAdmin, hasStudyInDesign, roles } = capabilities
  const canEdit = hasAtLeast(roles, 'CLIENT_EDITOR')
  const contextApplies = Boolean(hasStudyInDesign) && canEdit

  return NAV_GROUPS.flatMap((group) => {
    const groupVisible =
      group.showWhen === undefined ||
      (group.showWhen === 'methodologyUser' && (isMethodologyUser || contextApplies)) ||
      (group.showWhen === 'researchOps' && isResearchOps) ||
      (group.showWhen === 'platformAdmin' && isPlatformAdmin)

    if (!groupVisible) return []

    const items = group.items.filter((item) => {
      if (item.requireRole) return roles.includes(item.requireRole)
      if (item.minRole) return isMethodologyUser || (contextApplies && Boolean(item.contextVisible))
      return true
    })

    return items.length ? [{ ...group, items }] : []
  })
}
